'use client'

import * as React from 'react'
import { api } from '@/lib/api'
import type { Listing, NotificationItem } from '@/lib/types'
import type { RToastData } from '@/components/roostr'

// Data layer for "What's new" (Roostr Web redesign Phase 4, rebuilt for
// volume 2026-07-15 per the updated design — notifications.jsx + shell.jsx).
// The page is now a scannable date-grouped inbox with non-destructive
// mark-seen plus an opt-in bounded swipe session, so this hook models:
//
// - items: listings across ALL fetched notifications (read AND unread),
//   newest-first, deduped by listing id — the newest carrying notification
//   defines an item's source/timestamp. Items whose newest carrier is READ
//   start as status 'seen' (that is what makes "seen" non-destructive in
//   production: seen rows are read notifications' listings, so they survive
//   reloads instead of vanishing from an unread-only queue).
// - statusMap overlays this session's actions: liked | skipped | seen.
// - markSeen(ids): bulk 'seen' with toast-undo. Server-side, a notification
//   is marked read once every one of its listings is handled — plus one
//   blanket read-all for the unscoped "Mark all seen" when the whole list is
//   known locally.
//
// LAZY HYDRATION (kept from the 2026-07-15 scale fix): notification pages are
// fetched WITHOUT listing hydration (multi-MB hydrated pages saturated the
// API; a light request queued behind them took 30s+). Listings hydrate
// per-card: the swipe session prefetches PREFETCH ahead, and list rows call
// requestHydration(id) as they enter the viewport.

export type ReviewVerdict = 'liked' | 'skipped'
export type ItemStatus = ReviewVerdict | 'seen'

export interface QueueItem {
  listingId: string
  /** search_name of the newest notification carrying this listing. */
  source: string
  /** created_at of the newest notification carrying this listing (ISO). */
  at: string
  /** Hydrated lazily — undefined until the per-card fetch lands. */
  listing?: Listing
  /** Session action, or 'seen' derived from server read-state. null = new. */
  status: ItemStatus | null
  /** True when `status` comes from THIS session's statusMap (has history /
   * undo); false for server-derived 'seen'. The swipe deck uses it to know
   * whether acting on a card will create an undoable entry. */
  actedThisSession: boolean
}

interface HistoryEntry {
  id: number
  ids: string[]
  action: ItemStatus
  /** True when the like actually saved (listing wasn't already in Liked). */
  didSave: boolean
}

interface ReviewState {
  statusMap: Record<string, ItemStatus>
  history: HistoryEntry[]
}

type ReviewEvent =
  | { type: 'act'; entryId: number; id: string; action: ReviewVerdict; didSave: boolean }
  | { type: 'seen'; entryId: number; ids: string[] }
  | { type: 'undo' }
  | { type: 'restore'; entryId: number }
  | { type: 'reset' }

const INITIAL: ReviewState = { statusMap: {}, history: [] }

function reducer(state: ReviewState, ev: ReviewEvent): ReviewState {
  switch (ev.type) {
    case 'act': {
      // Guard against double-fires (e.g. a keydown racing a click) — an item
      // can only be acted on while it is still pending.
      if (state.statusMap[ev.id]) return state
      return {
        statusMap: { ...state.statusMap, [ev.id]: ev.action },
        history: [...state.history, { id: ev.entryId, ids: [ev.id], action: ev.action, didSave: ev.didSave }],
      }
    }
    case 'seen': {
      const ids = ev.ids.filter((id) => !state.statusMap[id])
      if (!ids.length) return state
      const statusMap = { ...state.statusMap }
      ids.forEach((id) => {
        statusMap[id] = 'seen'
      })
      return {
        statusMap,
        history: [...state.history, { id: ev.entryId, ids, action: 'seen', didSave: false }],
      }
    }
    case 'undo': {
      const last = state.history[state.history.length - 1]
      if (!last) return state
      const statusMap = { ...state.statusMap }
      last.ids.forEach((id) => {
        delete statusMap[id]
      })
      return { statusMap, history: state.history.slice(0, -1) }
    }
    case 'restore': {
      // Toast-undo of a specific bulk entry (may no longer be the last
      // history entry if the user kept acting while the toast was up).
      const entry = state.history.find((e) => e.id === ev.entryId)
      if (!entry) return state
      const statusMap = { ...state.statusMap }
      entry.ids.forEach((id) => {
        delete statusMap[id]
      })
      return { statusMap, history: state.history.filter((e) => e.id !== ev.entryId) }
    }
    case 'reset':
      return INITIAL
  }
}

// Non-hydrated pages are a few KB each, so the cap covers heavy accounts
// (backend limit max = 100). Read notifications are kept (they are the
// browsable "seen" tail of the list).
const PAGE_LIMIT = 100
const MAX_PAGES = 15 // sanity cap: 1500 notifications
// Rolling window (user-directed 2026-07-16): the stack holds the newest
// STACK_CAP listings — arrivals push in at the top, the oldest age out.
// Pagination stops as soon as the window is covered (usually 1-2 pages
// instead of all 15); the server mirrors the same cap with a lazy
// delete-based trim (search-api notification_stack_cap).
export const STACK_CAP = 500
// Swipe-session hydration lookahead + retry budget for transient failures.
const PREFETCH = 5
const MAX_LISTING_ATTEMPTS = 3
const HYDRATION_BATCH_SIZE = 100
const HYDRATION_BATCH_DELAY_MS = 16
// Live updates while the page stays open: the producer is a 15-min cron
// worker, so a 60s poll of page 1 (non-hydrated, a few KB) gives ≤1min
// freshness with no push infrastructure. Arrivals are BUFFERED — the page
// offers a "Show N new" pill instead of reshuffling rows (or an active swipe
// deck) underfoot.
const LIVE_POLL_MS = 60_000
const LIVE_POLL_MIN_GAP_MS = 15_000

/** Fired by the header bell's "Mark all seen" so a mounted page re-syncs. */
export const WHATS_NEW_SEEN_ALL_EVENT = 'roostr:whats-new-seen-all'

export function useReviewQueue(
  enabled: boolean,
  showToast: (toast: RToastData) => void,
  seenLabel: (n: number) => string
) {
  const [notifications, setNotifications] = React.useState<NotificationItem[] | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)
  const [state, dispatch] = React.useReducer(reducer, INITIAL)
  const [unsaveIds, setUnsaveIds] = React.useState<string[]>([])

  // Lazy listing hydration state. `missing` = confirmed 404 (delisted) —
  // dropped from the list AND counts as handled for read-marking (mirrors
  // the old hydration-drop + mobile §26.4's not-viewable rule). `unavailable`
  // = gave up after transient failures — dropped so the deck can advance,
  // but NOT counted as handled (the notification stays unread).
  const [listings, setListings] = React.useState<Record<string, Listing>>({})
  const [missingIds, setMissingIds] = React.useState<Set<string>>(() => new Set())
  const [unavailableIds, setUnavailableIds] = React.useState<Set<string>>(() => new Set())
  const [retryTick, setRetryTick] = React.useState(0)
  const inFlightRef = React.useRef<Set<string>>(new Set())
  const attemptsRef = React.useRef<Map<string, number>>(new Map())
  const queuedHydrationIdsRef = React.useRef<Set<string>>(new Set())
  const hydrationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushHydrationRef = React.useRef<() => void>(() => {})

  // Arrivals detected by the live poll, held until the page applies them.
  const [incoming, setIncoming] = React.useState<NotificationItem[]>([])

  // Latest state for callbacks driven by (possibly stale) keyboard closures.
  const stateRef = React.useRef(state)
  stateRef.current = state
  // Latest fetched lists for the poller's diff (poll callbacks are stable).
  const notificationsRef = React.useRef(notifications)
  notificationsRef.current = notifications
  const incomingRef = React.useRef(incoming)
  incomingRef.current = incoming
  const lastPollRef = React.useRef(0)
  const entrySeq = React.useRef(0)
  // Notifications already marked read on the server (one-way — see below).
  const markedRef = React.useRef<Set<string>>(new Set())
  // True once pagination reached the end of the server list — only then is
  // every unread notification known locally (gates the blanket read-all).
  const reachedEndRef = React.useRef(false)
  // Stale-guard: bumped on every fetch AND on sign-out, so responses landing
  // late (an older fetch, or after sign-out/account switch) are discarded
  // instead of clobbering the fresh state.
  const fetchSeq = React.useRef(0)

  const fetchQueue = React.useCallback(async () => {
    const seq = ++fetchSeq.current
    const isCurrent = () => seq === fetchSeq.current
    setLoading(true)
    setError(false)
    setIncoming([])
    reachedEndRef.current = false
    try {
      // First page paints the list fast…
      const first = await api.getNotifications(PAGE_LIMIT, 0, false)
      if (!isCurrent()) return
      let all = first.notifications
      let total = first.total
      setNotifications(all)
      // …then the remaining pages append progressively so counts and bulk
      // actions eventually see the whole list, not just the first page —
      // stopping early once the rolling window is covered (the stack shows
      // the newest STACK_CAP listings; anything older has aged out anyway).
      const windowIds = new Set<string>()
      first.notifications.forEach((n) => (n.listing_ids ?? []).forEach((id) => windowIds.add(id)))
      let page = 1
      let lastLen = first.notifications.length
      while (all.length < total && lastLen > 0 && page < MAX_PAGES && windowIds.size < STACK_CAP) {
        const res = await api.getNotifications(PAGE_LIMIT, page * PAGE_LIMIT, false)
        if (!isCurrent()) return
        lastLen = res.notifications.length
        total = res.total
        // New notifications arriving between page fetches shift offsets and
        // can repeat an already-fetched notification — drop the dupes.
        const seenIds = new Set(all.map((n) => n.id))
        const fresh = res.notifications.filter((n) => !seenIds.has(n.id))
        fresh.forEach((n) => (n.listing_ids ?? []).forEach((id) => windowIds.add(id)))
        all = [...all, ...fresh]
        setNotifications(all)
        page++
      }
      // An empty page means the server has nothing more even if `total`
      // disagrees (concurrent deletes); hitting MAX_PAGES leaves unknown
      // notifications server-side (reachedEnd stays false).
      reachedEndRef.current = all.length >= total || lastLen === 0
    } catch {
      if (isCurrent()) setError(true)
    } finally {
      if (isCurrent()) setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (enabled) {
      void fetchQueue()
    } else {
      // Signed out: invalidate any in-flight fetch and drop everything so a
      // later sign-in starts clean.
      fetchSeq.current++
      setNotifications(null)
      setIncoming([])
      setUnsaveIds([])
      setListings({})
      setMissingIds(new Set())
      setUnavailableIds(new Set())
      inFlightRef.current = new Set()
      attemptsRef.current = new Map()
      queuedHydrationIdsRef.current = new Set()
      if (hydrationTimerRef.current) clearTimeout(hydrationTimerRef.current)
      hydrationTimerRef.current = null
      markedRef.current = new Set()
      reachedEndRef.current = false
      setLoading(false)
      setError(false)
      dispatch({ type: 'reset' })
    }
  }, [enabled, fetchQueue])

  // The bell's "Mark all seen" is a blanket server read-all from outside this
  // hook — re-sync so a mounted page reflects it.
  React.useEffect(() => {
    if (!enabled) return
    const h = () => void fetchQueue()
    window.addEventListener(WHATS_NEW_SEEN_ALL_EVENT, h)
    return () => window.removeEventListener(WHATS_NEW_SEEN_ALL_EVENT, h)
  }, [enabled, fetchQueue])

  // Live poll while the page stays open: diff page 1 against everything known
  // and buffer genuinely new notifications. Skipped while hidden; a
  // visibility/focus return revalidates immediately (min-gap guarded so rapid
  // tab-switches don't stack requests).
  const pollForNew = React.useCallback(async () => {
    if (notificationsRef.current === null) return // initial fetch owns the state
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    if (Date.now() - lastPollRef.current < LIVE_POLL_MIN_GAP_MS) return
    lastPollRef.current = Date.now()
    const seq = fetchSeq.current
    try {
      const res = await api.getNotifications(PAGE_LIMIT, 0, false)
      if (seq !== fetchSeq.current) return // a refetch/sign-out superseded us
      const known = new Set([
        ...(notificationsRef.current ?? []).map((n) => n.id),
        ...incomingRef.current.map((n) => n.id),
      ])
      const fresh = res.notifications.filter((n) => !known.has(n.id))
      if (fresh.length) setIncoming((prev) => [...fresh, ...prev])
    } catch {
      // Transient failure — the next tick retries.
    }
  }, [])

  React.useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => void pollForNew(), LIVE_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void pollForNew()
    }
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled, pollForNew])

  /** Merge buffered arrivals into the list (the page's "Show N new" pill). */
  const applyIncoming = React.useCallback(() => {
    const fresh = incomingRef.current
    if (!fresh.length) return
    setIncoming([])
    setNotifications((prev) => {
      const seen = new Set(fresh.map((n) => n.id))
      return [...fresh, ...(prev ?? []).filter((n) => !seen.has(n.id))]
    })
  }, [])

  // Item building: ALL notifications newest-first, flatten + dedupe listing
  // ids — the newest carrier wins (defines source/at, and its read-state
  // seeds 'seen'). The stack is a ROLLING WINDOW: only the newest STACK_CAP
  // listings become items; older ones age out (truncated flags it for the
  // footer hint). notifMembers carries every UNREAD notification's (deduped)
  // listing ids for read-marking; unread notifications with no listing_ids at
  // all can never be reviewed, so a separate effect clears their phantom
  // unread.
  const { baseItems, windowIds, truncated, initialSeen, notifMembers, emptyNotifIds } = React.useMemo(() => {
    const sorted = (notifications ?? [])
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    const seen = new Set<string>()
    const items: Array<{ listingId: string; source: string; at: string }> = []
    const initial = new Set<string>()
    let overflow = false
    sorted.forEach((n) => {
      ;(n.listing_ids ?? []).forEach((id) => {
        if (seen.has(id)) return
        if (items.length >= STACK_CAP) {
          overflow = true
          return
        }
        seen.add(id)
        items.push({ listingId: id, source: n.search_name || 'Saved search', at: n.created_at })
        if (n.read) initial.add(id)
      })
    })
    const members: Array<{ id: string; members: string[] }> = []
    const empty: string[] = []
    sorted.forEach((n) => {
      if (n.read) return
      const m = Array.from(new Set(n.listing_ids ?? []))
      if (m.length > 0) members.push({ id: n.id, members: m })
      else empty.push(n.id)
    })
    return {
      baseItems: items,
      windowIds: seen,
      truncated: overflow,
      initialSeen: initial,
      notifMembers: members,
      emptyNotifIds: empty,
    }
  }, [notifications])

  // Ids that resolved missing/unavailable leave the list entirely — same net
  // effect as the old server-side hydration drop.
  const items = React.useMemo(
    () =>
      baseItems
        .filter((it) => !missingIds.has(it.listingId) && !unavailableIds.has(it.listingId))
        .map(
          (it): QueueItem => ({
            ...it,
            listing: listings[it.listingId],
            status: state.statusMap[it.listingId] ?? (initialSeen.has(it.listingId) ? 'seen' : null),
            actedThisSession: state.statusMap[it.listingId] != null,
          })
        ),
    [baseItems, missingIds, unavailableIds, listings, state.statusMap, initialSeen]
  )

  const pending = React.useMemo(() => items.filter((it) => it.status === null), [items])

  // Unique unread listing ids the buffer would add — drives the "Show N new"
  // pill. Buffers that add nothing visible (all duplicates/read) merge
  // silently so they can't rot.
  const incomingCount = React.useMemo(() => {
    if (!incoming.length) return 0
    const knownListings = new Set(baseItems.map((it) => it.listingId))
    const fresh = new Set<string>()
    incoming.forEach((n) => {
      if (n.read) return
      ;(n.listing_ids ?? []).forEach((id) => {
        if (!knownListings.has(id)) fresh.add(id)
      })
    })
    return fresh.size
  }, [incoming, baseItems])

  React.useEffect(() => {
    if (incoming.length > 0 && incomingCount === 0) applyIncoming()
  }, [incoming.length, incomingCount, applyIncoming])

  // One shared, short-window batch used by both the swipe-session prefetch and
  // list-row viewport hydration. This retains lazy loading while collapsing
  // simultaneous row requests into one ES _mget. Missing ids are delisted;
  // transient failures retry when retryTick re-arms the effects.
  const scheduleHydrationFlush = React.useCallback(() => {
    if (hydrationTimerRef.current) return
    hydrationTimerRef.current = setTimeout(() => {
      hydrationTimerRef.current = null
      flushHydrationRef.current()
    }, HYDRATION_BATCH_DELAY_MS)
  }, [])

  const flushHydration = React.useCallback(() => {
    const listingIds = Array.from(queuedHydrationIdsRef.current).slice(0, HYDRATION_BATCH_SIZE)
    if (!listingIds.length) return
    listingIds.forEach((id) => queuedHydrationIdsRef.current.delete(id))

    api
      .getListings(listingIds)
      .then((resolved) => {
        const resolvedIds = new Set(resolved.map((listing) => listing.id))
        setListings((prev) => ({
          ...prev,
          ...Object.fromEntries(resolved.map((listing) => [listing.id, listing])),
        }))
        const missing = listingIds.filter((id) => !resolvedIds.has(id))
        if (missing.length) {
          setMissingIds((prev) => new Set([...prev, ...missing]))
        }
      })
      .catch(() => {
        const exhausted = listingIds.filter((id) => (attemptsRef.current.get(id) ?? 0) >= MAX_LISTING_ATTEMPTS)
        if (exhausted.length) {
          setUnavailableIds((prev) => new Set([...prev, ...exhausted]))
        }
        if (exhausted.length !== listingIds.length) {
          setTimeout(() => setRetryTick((t) => t + 1), 1500)
        }
      })
      .finally(() => {
        listingIds.forEach((id) => inFlightRef.current.delete(id))
      })

    if (queuedHydrationIdsRef.current.size) scheduleHydrationFlush()
  }, [scheduleHydrationFlush])

  flushHydrationRef.current = flushHydration

  const hydrateOne = React.useCallback((listingId: string) => {
    if (inFlightRef.current.has(listingId)) return
    const attempts = attemptsRef.current.get(listingId) ?? 0
    if (attempts >= MAX_LISTING_ATTEMPTS) return
    inFlightRef.current.add(listingId)
    attemptsRef.current.set(listingId, attempts + 1)
    queuedHydrationIdsRef.current.add(listingId)
    scheduleHydrationFlush()
  }, [scheduleHydrationFlush])

  /** List rows call this as they enter the viewport (no-op once cached). */
  const requestHydration = React.useCallback(
    (listingId: string) => {
      hydrateOne(listingId)
    },
    [hydrateOne]
  )

  // Swipe-session lookahead: the newest pending cards hydrate ahead of the
  // deck so advancing is instant.
  React.useEffect(() => {
    pending.slice(0, PREFETCH).forEach(({ listingId, listing }) => {
      if (!listing) hydrateOne(listingId)
    })
  }, [pending, retryTick, hydrateOne])

  // Zero-listing cleanup: an unread notification with no listing ids can
  // never be reviewed, so it would sit as phantom unread forever — inflating
  // the bell badge with nothing to show.
  React.useEffect(() => {
    if (loading) return
    emptyNotifIds.forEach((id) => {
      if (markedRef.current.has(id)) return
      markedRef.current.add(id)
      api.markNotificationRead(id).catch(() => {
        // Allow a retry on the next pass.
        markedRef.current.delete(id)
      })
    })
  }, [loading, emptyNotifIds])

  // Read-state mapping: a notification is done once every one of its listing
  // ids is handled — acted on this session (liked/skipped/seen), already seen
  // via a newer read notification (initialSeen), confirmed delisted (404), or
  // AGED OUT of the rolling window (out-of-window ids can never be reviewed,
  // so they must not hold their notification unread forever). Gated on
  // !loading so a half-paginated window can't prematurely age things out.
  // Marking is ONE-WAY: undo restores items locally, but there is no un-read
  // endpoint, so the server read-state stays read. The list keeps working
  // because it is driven by local state.
  React.useEffect(() => {
    if (loading) return
    notifMembers.forEach(({ id, members }) => {
      if (markedRef.current.has(id)) return
      if (
        !members.every(
          (m) => state.statusMap[m] || initialSeen.has(m) || missingIds.has(m) || !windowIds.has(m)
        )
      )
        return
      markedRef.current.add(id)
      api.markNotificationRead(id).catch(() => {
        // Allow a retry on the next status change.
        markedRef.current.delete(id)
      })
    })
  }, [loading, state.statusMap, notifMembers, initialSeen, missingIds, windowIds])

  const act = React.useCallback((item: QueueItem, action: ReviewVerdict, didSave = false) => {
    dispatch({ type: 'act', entryId: ++entrySeq.current, id: item.listingId, action, didSave })
  }, [])

  /**
   * Non-destructive mark-seen with toast undo (local-only — see the marking
   * effect for the one-way server read-state). `blanket` (unscoped Mark-all
   * with the whole server list known locally) collapses the per-notification
   * calls into one read-all.
   */
  const markSeen = React.useCallback(
    (ids: string[], opts?: { blanket?: boolean }) => {
      const fresh = ids.filter((id) => !stateRef.current.statusMap[id])
      if (!fresh.length) return
      const entryId = ++entrySeq.current
      dispatch({ type: 'seen', entryId, ids: fresh })
      if (opts?.blanket && reachedEndRef.current) {
        notifMembers.forEach((n) => markedRef.current.add(n.id))
        emptyNotifIds.forEach((id) => markedRef.current.add(id))
        api.markAllNotificationsRead().catch(() => {})
      }
      showToast({ msg: seenLabel(fresh.length), undo: () => dispatch({ type: 'restore', entryId }) })
    },
    [notifMembers, emptyNotifIds, showToast, seenLabel]
  )

  const undoLast = React.useCallback(() => {
    const history = stateRef.current.history
    const last = history[history.length - 1]
    if (!last) return
    dispatch({ type: 'undo' })
    // Un-save only likes that actually saved; server notification read-state
    // is not reverted (see the marking effect above).
    if (last.action === 'liked' && last.didSave) {
      setUnsaveIds((p) => [...p, ...last.ids.filter((id) => !p.includes(id))])
    }
  }, [])

  const consumeUnsave = React.useCallback((id: string) => {
    setUnsaveIds((p) => p.filter((x) => x !== id))
  }, [])

  return {
    /** False until the first fetch resolves (skeleton state). */
    ready: notifications !== null,
    loading,
    error,
    refetch: fetchQueue,
    /** Every listing in the inbox, newest first, with per-item status. */
    items,
    /** True when older matches were cut by the rolling STACK_CAP window. */
    truncated,
    /** Items with no status — the "new" set (badge unit, swipe source). */
    pending,
    hasHistory: state.history.length > 0,
    act,
    markSeen,
    undoLast,
    requestHydration,
    /** Unread listings waiting in the live-poll buffer (the "Show N new" pill). */
    incomingCount,
    applyIncoming,
    /** Listing ids awaiting an un-save (undone likes) — render UnsaveWorkers. */
    unsaveIds,
    consumeUnsave,
  }
}
